import { Inject, Injectable } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { ComponentStore } from "@ngrx/component-store";
import { merge, of, fromEvent, combineLatest } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";

import { DeviceType, PaymentMethods, VisitorState, Jurisdication } from "./visitor.model";
import { CoreService } from "../core.service";
import { paymentMethods } from "./visitor.config";

@Injectable({
  providedIn: "root",
})
export class VisitorStore extends ComponentStore<VisitorState> {
  constructor(@Inject(DOCUMENT) private document: Document, private coreService: CoreService) {
    super({ deviceType: undefined });
  }

  readonly jurisdication$ = this.select((state) => state.jurisdication);

  readonly currencyCode$ = this.select((state) => state.currencyCode);

  readonly currencySymbol$ = this.select((state) => state.currencySymbol);

  readonly countryCode$ = this.select((state) => state.countryCode);

  readonly deviceType$ = this.select((state) => state.deviceType);

  readonly paymentMethods$ = this.select((state) => state.paymentMethods);

  readonly exchangeRates$ = this.select((state) => state.exchangeRates);

  readonly updateJurisdication = this.updater(
    (state, jurisdication: VisitorState['jurisdication']) => ({ ...state, jurisdication })
  );

  readonly updateCurrencyCode = this.updater(
    (state, currencyCode: VisitorState['currencyCode']) => ({ ...state, currencyCode })
  );

  readonly updateCurrencySymbol = this.updater(
    (state, currencySymbol: VisitorState['currencySymbol']) => ({ ...state, currencySymbol })
  );

  readonly updateCountryCode = this.updater(
    (state, countryCode: VisitorState['countryCode']) => ({ ...state, countryCode })
  );

  readonly updateDeviceType = this.updater(
    (state, deviceType: DeviceType) => ({ ...state, deviceType })
  );

  readonly updatePaymentMethods = this.updater((state, paymentMethods: PaymentMethods[]) => ({
    ...state, paymentMethods: paymentMethods
  }));

  readonly updateEchangeRates = this.updater((state, exchangeRates: VisitorState['exchangeRates']) => ({ 
    ...state, exchangeRates: exchangeRates 
  }));


  readonly getDeviceType = this.effect(() => {
    const breakpoint = window
      .getComputedStyle(this.document.body)
      .getPropertyValue("--breakpoint-md");
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint})`);
    return merge(
      of(mediaQuery),
      fromEvent<MediaQueryListEvent>(mediaQuery, "change")
    ).pipe(
      map((query) => {
        return query.matches ? DeviceType.Mobile : DeviceType.Desktop;
      }),
      tap<DeviceType>((deviceType) => this.updateDeviceType(deviceType))
    );
  });

  readonly filterAllowedPaymentMethods = this.effect(() => {
    return combineLatest([this.countryCode$, this.jurisdication$]).pipe(
      switchMap(([countryCode, jurisdiction]) => {
        const allowedMethods = this.coreService.filterArrayByLocation(
          paymentMethods,
          jurisdiction,
          countryCode
        );

        const methodsNameAndIcon = allowedMethods.map(({ name, icon }) => ({
          name,
          icon,
        }));

        return of(methodsNameAndIcon);
      }),
      tap<PaymentMethods[]>((allowedMethods) =>
        this.updatePaymentMethods(allowedMethods)
      )
    );
  });
}
